import React from 'react';
import styled from '@emotion/styled';
import {
  HeadingText40,
  Input,
  InputType,
  HeadingText30,
  SelectField,
} from '@modernhealth/vital-components';

import { CircleRsvpUnauthenticatedUserInfoInput } from 'src/generated/graphql';
import countries from 'src/shared/data/countries.json';

import { isEmailValid } from './utils/emailValidation';

export interface UserInfoProps {
  formValues: Partial<CircleRsvpUnauthenticatedUserInfoInput>;
  setFormValue: (field: string, value: string | boolean) => void;
  showErrors: boolean;
}
const UserDetailsWrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  margin: '32px 0 24px 0',
  gap: '16px',
});

const InputWrapper = styled.div({
  ':not(:last-child)': {
    marginBottom: '16px',
  },
});

const RadioInputWrapper = styled(InputWrapper)({
  display: 'flex',
  flexDirection: 'column',
});

const StyledLabel = styled.p({
  marginBottom: '16px',
});

const StyledSection = styled.div({
  ':not(:last-child)': {
    marginBottom: '40px',
  },
});

const UserInfo: React.FC<UserInfoProps> = ({
  setFormValue,
  formValues: { firstName, lastName, email, company, country, isMember },

  showErrors,
}) => {
  const getError = (label?: string, errorMsg?: string) => {
    if (errorMsg) return errorMsg;
    return label ? `${label} is required` : 'Field is required';
  };

  const countryOptions = countries.map(country => {
    return { label: country.name, value: country.name };
  });

  return (
    <UserDetailsWrapper>
      <StyledSection>
        <HeadingText40 css={{ marginBottom: '32px' }}>Register as a guest</HeadingText40>
        <RadioInputWrapper></RadioInputWrapper>
        <InputWrapper>
          <StyledLabel>
            <HeadingText30 as="span">Employer or company name</HeadingText30>
          </StyledLabel>
          <Input
            useDefaultPlaceholder
            type={InputType.TextField}
            onChange={(value: string | React.ChangeEvent<HTMLInputElement>) => {
              setFormValue('company', value.toString());
            }}
            label="Employer or company name"
            fieldId="company"
            value={company ?? ''}
            css={{ marginBottom: '16px' }}
            helpText="Optional"
            required={isMember}
            error={!company && showErrors && isMember ? 'This question is required' : undefined}
          />
        </InputWrapper>
      </StyledSection>
      <StyledSection>
        <HeadingText40 css={{ marginBottom: '32px' }}>Personal Information</HeadingText40>
        <InputWrapper>
          <StyledLabel>
            <HeadingText30 as="span">First name</HeadingText30>
          </StyledLabel>
          <Input
            useDefaultPlaceholder
            type={InputType.TextField}
            label="First name"
            fieldId="firstname"
            value={firstName}
            required
            onChange={(value: string | React.ChangeEvent<HTMLInputElement>) => {
              setFormValue('firstName', value.toString());
            }}
            error={!firstName && showErrors ? getError('First name') : undefined}
          />
        </InputWrapper>
        <InputWrapper>
          <StyledLabel>
            <HeadingText30 as="span">Last name</HeadingText30>
          </StyledLabel>
          <Input
            useDefaultPlaceholder
            type={InputType.TextField}
            label="Last name"
            fieldId="lastname"
            value={lastName}
            required
            onChange={(value: string | React.ChangeEvent<HTMLInputElement>) => {
              setFormValue('lastName', value.toString());
            }}
            error={!lastName && showErrors ? getError('Last name') : undefined}
          />
        </InputWrapper>
        <InputWrapper>
          <StyledLabel>
            <HeadingText30 as="span">Email Address</HeadingText30>
          </StyledLabel>
          <Input
            useDefaultPlaceholder
            type={InputType.TextField}
            label="Enter your email address"
            fieldId="email"
            required
            value={email}
            helpText="We'll send your invite to this email"
            onChange={(value: string | React.ChangeEvent<HTMLInputElement>) => {
              setFormValue('email', value.toString());
            }}
            error={
              !(email && isEmailValid(email)) && showErrors
                ? getError(undefined, 'A valid email is required')
                : undefined
            }
          />
        </InputWrapper>
        <InputWrapper>
          <label id="countryLabel">
            <HeadingText30 as="span">Country</HeadingText30>
            <div css={{ margin: '16px 0 4px 0' }}>
              <SelectField
                options={countryOptions}
                value={country}
                fieldId="country"
                labelledBy="countryLabel"
                placeholder="Select your country"
                error={showErrors && !country ? 'Country is required' : undefined}
                onChange={option => {
                  setFormValue('country', option?.value || '');
                }}
              />
            </div>
          </label>
        </InputWrapper>
      </StyledSection>
    </UserDetailsWrapper>
  );
};

export default UserInfo;
